import type { ExtractedProgramType } from '@shared/models/extracted-design-facts.model';
import type { FfuProgram } from '@shared/models/ffu-output.model';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import {
  compareStoreyRows,
  formatStoreyPlacement,
  isEntranceStorey,
  storeyLabel,
  type BuildingStoreyRow,
} from './plan-storey-previews.helpers';

const PROGRAM_LABEL: Record<ExtractedProgramType, string> = {
  residential: 'Bostad',
  office: 'Kontor',
  industry: 'Industri',
  retail: 'Handel',
  school: 'Skola',
  garage: 'Garage',
  stairwells: 'Trapphus',
  other: 'Övrigt',
};

const formatCount = (n: number): string =>
  Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10);

const formatMeters = (n: number): string => `${formatCount(n)} m`;

const formatArea = (n: number): string => `${formatCount(n)} m²`;

const formatSignedMeters = (n: number): string => {
  const text = formatCount(Math.abs(n));
  if (n > 0) return `+${text} m`;
  if (n < 0) return `−${text} m`;
  return '0 m';
};

const formatShare = (share: number): string => `${Math.round(share * 100)} %`;

/**
 * Nodon program types with their share, one per type: `Bostad 90 %, Övrigt 10 %`.
 * Several rows of one type (printed `other` rooms) add up. Printed names and
 * areas are not shown.
 */
const contentCell = (storey: BuildingStoreyRow): string => {
  const byType = new Map<FfuProgram['type'], number | null>();
  for (const program of storey.activities) {
    const current = byType.get(program.type);
    if (!byType.has(program.type)) {
      byType.set(program.type, program.share);
      continue;
    }
    byType.set(
      program.type,
      current == null || program.share == null
        ? (current ?? program.share)
        : current + program.share,
    );
  }
  return [...byType.entries()]
    .map(([type, share]) =>
      share == null
        ? PROGRAM_LABEL[type]
        : `${PROGRAM_LABEL[type]} ${formatShare(share)}`,
    )
    .join(', ');
};

type FactField = {
  id: string;
  label: string;
  align: 'left' | 'right';
  /** Stays on the row when every cell is empty. */
  always: boolean;
  value: (row: BuildingStoreyRow) => string;
};

const FACT_FIELDS: readonly FactField[] = [
  {
    id: 'placement',
    label: 'Placering',
    align: 'right',
    always: true,
    value: formatStoreyPlacement,
  },
  {
    id: 'area',
    label: 'Area',
    align: 'right',
    always: false,
    value: (row) => (row.gfa == null ? '' : formatArea(row.gfa)),
  },
  {
    id: 'content',
    label: 'Innehåll',
    align: 'left',
    always: true,
    value: contentCell,
  },
  {
    id: 'innerHeight',
    label: 'Rumshöjd',
    align: 'right',
    always: true,
    value: (row) =>
      row.inner_height == null ? '' : formatMeters(row.inner_height),
  },
  {
    id: 'floorToFloor',
    label: 'Våningshöjd',
    align: 'right',
    always: true,
    value: (row) => (row.height == null ? '' : formatMeters(row.height)),
  },
  {
    id: 'fromGround',
    label: 'Från mark',
    align: 'right',
    always: false,
    value: (row) =>
      row.elevation == null ? '' : formatSignedMeters(row.elevation),
  },
  {
    id: 'mark',
    label: 'Höjd',
    align: 'right',
    always: false,
    value: (row) =>
      row.elevationMarkM == null ? '' : formatSignedMeters(row.elevationMarkM),
  },
];

export type StoreyTableColumn = {
  id: string;
  /** Leaf header. The fact name when the column is shared, otherwise the part name. */
  label: string;
  align: 'left' | 'right';
};

export type StoreyTableHeaderCell = {
  id: string;
  label: string;
  align: 'left' | 'right';
  colSpan: number;
  rowSpan: number;
};

export type StoreyTableRow = {
  key: string;
  entrance: boolean;
  cells: readonly string[];
};

export type StoreyTable = {
  columns: readonly StoreyTableColumn[];
  /** One row when every fact is shared. Two when a fact is split by part. */
  headerRows: readonly (readonly StoreyTableHeaderCell[])[];
  rows: readonly StoreyTableRow[];
};

const partToken = (partName: string | null): string => partName ?? '';

/** Same index is one storey (parts' storeys pair on index). Without an index, the plan numbers. */
const storeyIdentity = (row: BuildingStoreyRow): string => {
  if (row.index !== null) return `index:${row.index}`;
  if (row.planNumbers.length > 0) {
    return `plan:${[...row.planNumbers].sort((a, b) => a - b).join('+')}`;
  }
  return `name:${row.name ?? ''}`;
};

const fillFacts = (
  current: BuildingStoreyRow,
  next: BuildingStoreyRow,
): BuildingStoreyRow => ({
  ...current,
  name: current.name ?? next.name,
  inner_height: current.inner_height ?? next.inner_height,
  height: current.height ?? next.height,
  gfa: current.gfa ?? next.gfa,
  activities:
    current.activities.length > 0 ? current.activities : next.activities,
  elevationMarkM: current.elevationMarkM ?? next.elevationMarkM,
  elevation: current.elevation ?? next.elevation,
});

const partsIn = (rows: readonly BuildingStoreyRow[]): (string | null)[] => {
  const seen = new Set<string>();
  const parts: (string | null)[] = [];
  for (const row of rows) {
    const token = partToken(row.partName);
    if (seen.has(token)) continue;
    seen.add(token);
    parts.push(row.partName);
  }
  return parts;
};

type StoreyGroup = {
  key: string;
  members: BuildingStoreyRow[];
  byPart: Map<string, BuildingStoreyRow>;
};

/** One våning-table row: every part and reading of that storey. */
export type StoreyTableGroup = {
  key: string;
  members: readonly BuildingStoreyRow[];
};

const groupsFor = (rows: readonly BuildingStoreyRow[]): StoreyGroup[] => {
  const sorted = [...rows].sort(compareStoreyRows);
  const order: string[] = [];
  const members = new Map<string, BuildingStoreyRow[]>();
  for (const row of sorted) {
    const key = storeyIdentity(row);
    const list = members.get(key);
    if (list == null) {
      members.set(key, [row]);
      order.push(key);
      continue;
    }
    list.push(row);
  }
  return order.map((key) => {
    const groupMembers = members.get(key) ?? [];
    const byPart = new Map<string, BuildingStoreyRow>();
    for (const row of groupMembers) {
      const token = partToken(row.partName);
      const existing = byPart.get(token);
      if (existing == null) {
        byPart.set(token, row);
        continue;
      }
      byPart.set(token, fillFacts(existing, row));
    }
    return { key, members: groupMembers, byPart };
  });
};

const partValue = (
  group: StoreyGroup,
  partName: string | null,
  value: (row: BuildingStoreyRow) => string,
): string => {
  const row = group.byPart.get(partToken(partName));
  if (row == null) return '';
  return value(row);
};

const presentValues = (
  group: StoreyGroup,
  parts: readonly (string | null)[],
  value: (row: BuildingStoreyRow) => string,
): string[] =>
  parts.flatMap((part) => {
    const row = group.byPart.get(partToken(part));
    if (row == null) return [];
    return [value(row)];
  });

/** Parts that do not have this storey are absent, so they do not count as a different value. */
const valuesDiffer = (
  groups: readonly StoreyGroup[],
  parts: readonly (string | null)[],
  value: (row: BuildingStoreyRow) => string,
): boolean => {
  if (parts.length < 2) return false;
  return groups.some((group) => {
    const values = presentValues(group, parts, value);
    const first = values[0];
    if (first == null) return false;
    return values.some((item) => item !== first);
  });
};

const hasValue = (
  groups: readonly StoreyGroup[],
  value: (row: BuildingStoreyRow) => string,
): boolean =>
  groups.some((group) =>
    [...group.byPart.values()].some((row) => value(row) !== ''),
  );

type SharedColumn = {
  kind: 'shared';
  id: string;
  label: string;
  align: 'left' | 'right';
  cell: (group: StoreyGroup) => string;
};

type PartColumn = {
  kind: 'part';
  id: string;
  label: string;
  align: 'left' | 'right';
  groupId: string;
  groupLabel: string;
  cell: (group: StoreyGroup) => string;
};

type ColumnSpec = SharedColumn | PartColumn;

const headerCell = (
  spec: Pick<ColumnSpec, 'id' | 'label' | 'align'>,
  span: { colSpan: number; rowSpan: number },
): StoreyTableHeaderCell => ({
  id: spec.id,
  label: spec.label,
  align: spec.align,
  colSpan: span.colSpan,
  rowSpan: span.rowSpan,
});

const partRunEnd = (
  specs: readonly ColumnSpec[],
  start: number,
  groupId: string,
): number => {
  let end = start;
  while (end < specs.length) {
    const spec = specs[end];
    if (spec == null || spec.kind !== 'part') break;
    if (spec.groupId !== groupId) break;
    end += 1;
  }
  return end;
};

/** Shared facts span both header rows. A split fact is the group, then one part name each. */
const headerRowsFor = (
  specs: readonly ColumnSpec[],
): StoreyTableHeaderCell[][] => {
  const split = specs.some((spec) => spec.kind === 'part');
  if (!split) {
    return [specs.map((spec) => headerCell(spec, { colSpan: 1, rowSpan: 1 }))];
  }
  const top: StoreyTableHeaderCell[] = [];
  const bottom: StoreyTableHeaderCell[] = [];
  for (let index = 0; index < specs.length;) {
    const spec = specs[index];
    if (spec == null) break;
    switch (spec.kind) {
      case 'shared':
        top.push(headerCell(spec, { colSpan: 1, rowSpan: 2 }));
        index += 1;
        break;
      case 'part': {
        const end = partRunEnd(specs, index, spec.groupId);
        top.push(
          headerCell(
            { id: spec.groupId, label: spec.groupLabel, align: 'left' },
            { colSpan: end - index, rowSpan: 1 },
          ),
        );
        for (let leaf = index; leaf < end; leaf += 1) {
          const part = specs[leaf];
          if (part == null || part.kind !== 'part') continue;
          bottom.push(headerCell(part, { colSpan: 1, rowSpan: 1 }));
        }
        index = end;
        break;
      }
      default:
        return assertUnreachable(spec);
    }
  }
  return [top, bottom];
};

const sharedValue = (
  group: StoreyGroup,
  parts: readonly (string | null)[],
  value: (row: BuildingStoreyRow) => string,
): string =>
  parts
    .map((part) => partValue(group, part, value))
    .find((item) => item !== '') ?? '';

const partLeafLabel = (partName: string | null): string =>
  partName?.trim() ?? '';

const columnSpecs = (
  groups: readonly StoreyGroup[],
  parts: readonly (string | null)[],
): ColumnSpec[] => {
  const specs: ColumnSpec[] = [
    {
      kind: 'shared',
      id: 'plan',
      label: 'Plan',
      align: 'left',
      cell: (group) => {
        const representative = group.members[0];
        if (representative == null) return '';
        return storeyLabel(representative);
      },
    },
  ];
  for (const field of FACT_FIELDS) {
    const split = valuesDiffer(groups, parts, field.value);
    if (!field.always && !hasValue(groups, field.value)) continue;
    if (!split) {
      specs.push({
        kind: 'shared',
        id: field.id,
        label: field.label,
        align: field.align,
        cell: (group) => sharedValue(group, parts, field.value),
      });
      continue;
    }
    parts.forEach((partName, index) => {
      specs.push({
        kind: 'part',
        id: `${field.id}:${index}`,
        label: partLeafLabel(partName),
        align: field.align,
        groupId: field.id,
        groupLabel: field.label,
        cell: (group) => partValue(group, partName, field.value),
      });
    });
  }
  return specs;
};

/**
 * The rows {@link storeyTable} shows, in that order. Parts that share a plan
 * are one group. The plan preview uses this list and does not split it again.
 */
export const storeyGroups = (
  storeys: readonly BuildingStoreyRow[],
): StoreyTableGroup[] =>
  groupsFor(storeys).map(({ key, members }) => ({ key, members }));

/**
 * One row per storey. The same plan on two parts stays one row. A shared
 * fact stays one column. A fact that differs by part is one group: the fact
 * name, then one sub-column per part. Innehåll, Rumshöjd and Våningshöjd stay
 * when every cell is empty.
 */
export const storeyTable = (
  storeys: readonly BuildingStoreyRow[],
): StoreyTable => {
  const groups = groupsFor(storeys);
  const parts = partsIn([...storeys].sort(compareStoreyRows));
  const specs = columnSpecs(groups, parts);
  return {
    columns: specs.map(({ id, label, align }) => ({ id, label, align })),
    headerRows: headerRowsFor(specs),
    rows: groups.map((group) => ({
      key: group.key,
      entrance: group.members.some(isEntranceStorey),
      cells: specs.map((spec) => spec.cell(group)),
    })),
  };
};
